import { consume } from '@lit/context';
import { LitElement, css, html, nothing, unsafeCSS } from 'lit';
import { customElement } from 'lit/decorators.js';
import { keyed } from 'lit/directives/keyed.js';
import { repeat } from 'lit/directives/repeat.js';

import { gameApiContext } from '../../../context';
import type { GameAPIType } from '../../../GameAPI';
import {
  NOTICE_COLUMN_MAX_PX,
  NOTICE_COMPACT_MAX_WIDTH,
  NOTICE_FADE_MS,
  noticeStyles,
} from '../../../theme/notice.css';
import { tokens } from '../../../theme/tokens.css';
import { uiState as uiStateProxy } from '../../../UIState';
import { bindValtio } from '../../../ValtioController';
import { noticeFlagStyle, noticeName, renderNoticeBadge, renderNoticeTally } from '../notice-player';

/**
 * Kill Notice - "You have destroyed..." notification overlay (top 32%)
 *
 * Displays kill streak notifications with accumulating victims, score, and upgrades.
 * Each sub-element (player, score, upgrade) pops independently when it changes.
 * Auto-dismisses after timeout, with timer reset when new victims arrive. Once
 * the fade-out has started the state is told, so points or an upgrade arriving
 * then open a fresh notice rather than bringing this one back.
 *
 * @element kill-notice
 */
@customElement('kill-notice')
export class KillNotice extends LitElement {
  static override styles = [
    tokens,
    noticeStyles,
    css`
      /* The host, the pop/fade animations, the player chip, flag, badges and
         the score and upgrade lines come from noticeStyles, shared with
         death-notice; only the notice's placement and size are this one's. */
      .notice {
        top: 32%;
        width: 70%;
        left: 15%;
        font-size: 30px;
      }

      /* Tablet (same query as noticeStyles' chip rule) */
      @media (min-width: 600px) and (max-width: ${unsafeCSS(NOTICE_COMPACT_MAX_WIDTH)}px) {
        .notice {
          font-size: 26px;
        }
      }

      /* Phone, portrait or landscape — after tablet, which a landscape phone
         also matches. A block of airmash-game's notice column there, so the
         placement above is zeroed (noticeStyles has the rest). */
      @media (max-width: ${unsafeCSS(NOTICE_COLUMN_MAX_PX)}px), (max-height: ${unsafeCSS(NOTICE_COLUMN_MAX_PX)}px) {
        .notice {
          font-size: var(--font-size-xl);
          top: auto;
          left: auto;
          /* Wider than the desktop's 70%, so the chips wrap less and the
             column stays short */
          width: 90%;
        }
      }
    `,
  ];

  private uiState = bindValtio(this, uiStateProxy);

  @consume({ context: gameApiContext })
  private gameApi!: GameAPIType;

  private dismissTimer: number | null = null;
  private fadeTimer: number | null = null;
  private lastNoticeId: number = -1;
  private lastPlayerCount: number = 0;
  private lastScore: number | false = false;
  private lastUpgrades: number = 0;
  private hiding = false;
  private renderKey: number = 0;

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.clearTimers();
  }

  private clearTimers() {
    if (this.dismissTimer !== null) {
      window.clearTimeout(this.dismissTimer);
      this.dismissTimer = null;
    }
    if (this.fadeTimer !== null) {
      window.clearTimeout(this.fadeTimer);
      this.fadeTimer = null;
    }
  }

  override updated() {
    const notice = this.uiState.killNotice;

    if (!notice) {
      this.hiding = false;
      this.lastNoticeId = -1;
      this.lastPlayerCount = 0;
      this.lastScore = false;
      this.lastUpgrades = 0;
      this.clearTimers();
      return;
    }

    // Same notice ID — check if anything actually changed
    if (notice.id === this.lastNoticeId) {
      const dataChanged =
        notice.players.length !== this.lastPlayerCount ||
        notice.scoreChange !== this.lastScore ||
        notice.upgradesChange !== this.lastUpgrades;
      if (!dataChanged) return;
      // Data changed — cancel fade if mid-fade, reset timer.
      const wasHiding = this.hiding;
      if (wasHiding) this.renderKey++;
      this.lastPlayerCount = notice.players.length;
      this.lastScore = notice.scoreChange;
      this.lastUpgrades = notice.upgradesChange;
      this.hiding = false;
      this.clearTimers();
      // hiding/renderKey were flipped AFTER this cycle's render, so the DOM
      // still shows the fading, stale-keyed notice. Re-render so keyed()
      // rebuilds a fresh, non-fading notice for the new data (a kill that
      // lands during the 1.5s fade-out).
      if (wasHiding) this.requestUpdate();
    } else {
      // Brand new notice
      this.hiding = false;
      this.lastNoticeId = notice.id;
      this.lastPlayerCount = notice.players.length;
      this.lastScore = notice.scoreChange;
      this.lastUpgrades = notice.upgradesChange;
      this.clearTimers();
    }

    // Phase 1: after timeout, start fade-out
    const capturedId = notice.id;
    this.dismissTimer = window.setTimeout(() => {
      this.dismissTimer = null;
      const current = this.uiState.killNotice;
      if (!current || current.id !== capturedId) return;

      this.hiding = true;
      this.requestUpdate();
      // Told to the state so points or an upgrade arriving now open a fresh
      // notice instead of folding in here and bringing this one back
      this.gameApi.markKillNoticeFading(capturedId);

      // Phase 2: after fade animation, clear the state
      this.fadeTimer = window.setTimeout(() => {
        this.fadeTimer = null;
        const stillCurrent = this.uiState.killNotice;
        if (stillCurrent && stillCurrent.id === capturedId) {
          this.hiding = false;
          this.lastNoticeId = -1;
          this.gameApi.dismissKillNoticeIfMatching(capturedId);
        }
      }, NOTICE_FADE_MS);
    }, notice.timeoutMs);
  }

  override render() {
    const notice = this.uiState.killNotice;
    if (!notice) return nothing;

    const players = notice.players.length > 6 ? notice.players.slice(0, 6) : notice.players;
    // Legacy capped names only in a multi-victim streak; a single kill showed
    // the full name. The compact breakpoints cap too, where a long name
    // overflows the notice.
    const capNames = players.length > 1 || this.uiState.viewport.width <= NOTICE_COMPACT_MAX_WIDTH;
    // `hiding` belongs to the notice whose fade timer set it, and updated()
    // only learns of a replacement after this render: a notice that replaces
    // one mid-fade (a fresh streak, or points arriving once the fade began)
    // must not inherit the class, or it replays msghide and vanishes at once.
    const hiding = this.hiding && notice.id === this.lastNoticeId;

    return keyed(
      `${notice.id}-${this.renderKey}`,
      html`
        <div class="notice${hiding ? ' hiding' : ''}">
          ${players.length > 0 ? 'You have destroyed' : nothing}
          ${repeat(
            players,
            (p, i) => `${p.name}-${i}`,
            (p, i) => {
              const isNew = notice.id === this.lastNoticeId && i >= this.lastPlayerCount;
              return html`
                <span class="player${isNew ? ' pop' : ''}">
                  <span class="flag" style=${noticeFlagStyle(p.flag)}></span>
                  ${noticeName(p.name, capNames)}${renderNoticeBadge(p)}
                </span>
              `;
            },
          )}
          ${renderNoticeTally(notice.scoreChange, notice.upgradesChange)}
        </div>
      `,
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'kill-notice': KillNotice;
  }
}
